/**
 * Pending Verification Dashboard
 * Shows transactions awaiting approval/verification
 * Phase 7: Bank Transaction Management
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, CircularProgress, Alert, IconButton, Tooltip
} from '@mui/material';
import { Check, Close, Visibility, Refresh } from '@mui/icons-material';
import { BankTransactionAPI } from '../../../../services/bankTransactionService';

const PendingVerificationDashboard = ({ onRefresh }) => {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [error, setError] = useState('');
  const [selectedTx, setSelectedTx] = useState(null);
  const [verifyDialog, setVerifyDialog] = useState({ open: false, approved: false });
  const [notes, setNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadPendingTransactions();
  }, []);

  const loadPendingTransactions = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await BankTransactionAPI.getPendingVerification();
      setTransactions(data.transactions || []);
    } catch (err) {
      setError('Failed to load pending transactions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!selectedTx) return;

    setProcessing(true);
    try {
      await BankTransactionAPI.verifyTransaction(selectedTx.id, {
        approved: verifyDialog.approved,
        notes,
        rejectionReason: verifyDialog.approved ? '' : rejectionReason
      });

      setVerifyDialog({ open: false, approved: false });
      setSelectedTx(null);
      setNotes('');
      setRejectionReason('');
      loadPendingTransactions();
      onRefresh?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to verify transaction');
    } finally {
      setProcessing(false);
    }
  };

  const openVerifyDialog = (tx, approved) => {
    setSelectedTx(tx);
    setVerifyDialog({ open: true, approved });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED'
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-AE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Pending Verification ({transactions.length})
        </Typography>
        <IconButton onClick={loadPendingTransactions} size="small">
          <Refresh />
        </IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {transactions.length === 0 ? (
        <Typography color="textSecondary" align="center" py={3}>
          No pending transactions
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Account</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Category</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Recorded By</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((tx) => (
                <TableRow key={tx.id} hover>
                  <TableCell>{formatDate(tx.transactionDate)}</TableCell>
                  <TableCell>{tx.bankAccountName}</TableCell>
                  <TableCell>
                    <Chip
                      label={tx.transactionType?.replace(/_/g, ' ')}
                      size="small"
                      color="default"
                    />
                  </TableCell>
                  <TableCell>{tx.category || '-'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatAmount(tx.amount)}
                  </TableCell>
                  <TableCell>{tx.recordedByName || '-'}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Approve">
                      <IconButton
                        color="success"
                        size="small"
                        onClick={() => openVerifyDialog(tx, true)}
                      >
                        <Check />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reject">
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => openVerifyDialog(tx, false)}
                      >
                        <Close />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Verify Dialog */}
      <Dialog open={verifyDialog.open} onClose={() => setVerifyDialog({ open: false, approved: false })}>
        <DialogTitle>
          {verifyDialog.approved ? 'Approve Transaction' : 'Reject Transaction'}
        </DialogTitle>
        <DialogContent>
          {selectedTx && (
            <Box mb={2}>
              <Typography variant="body2">
                <strong>Amount:</strong> {formatAmount(selectedTx.amount)}
              </Typography>
              <Typography variant="body2">
                <strong>Type:</strong> {selectedTx.transactionType?.replace(/_/g, ' ')}
              </Typography>
              <Typography variant="body2">
                <strong>Description:</strong> {selectedTx.description || '-'}
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            multiline
            rows={2}
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ mb: 2 }}
          />

          {!verifyDialog.approved && (
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Rejection Reason *"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              error={!rejectionReason}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyDialog({ open: false, approved: false })}>
            Cancel
          </Button>
          <Button
            onClick={handleVerify}
            variant="contained"
            color={verifyDialog.approved ? 'success' : 'error'}
            disabled={processing || (!verifyDialog.approved && !rejectionReason)}
          >
            {processing ? <CircularProgress size={20} /> : (verifyDialog.approved ? 'Approve' : 'Reject')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PendingVerificationDashboard;
